/**
 * AP Reference Number Lookup Tool
 * Smart auto-detect: 5-digit model codes OR full 17-character references
 * Security: DOMPurify, honeypot, input validation
 */

(function() {
  'use strict';

  // Wait for DOM and DOMPurify to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    // Check if DOMPurify is available
    if (typeof DOMPurify === 'undefined') {
      console.error('DOMPurify is not loaded. AP lookup tool requires DOMPurify for security.');
      return;
    }

    // Find all AP lookup sections on the page
    const sections = document.querySelectorAll('.ap-lookup-section');

    sections.forEach(section => {
      const sectionId = section.getAttribute('data-section-id');
      if (sectionId) {
        initializeSection(sectionId);
      }
    });
  }

  function initializeSection(sectionId) {
    // Get configuration from JSON
    const configElement = document.getElementById(`ap-lookup-config-${sectionId}`);
    if (!configElement) {
      console.error(`Configuration not found for section ${sectionId}`);
      return;
    }

    let config;
    try {
      config = JSON.parse(configElement.textContent);
    } catch (e) {
      console.error('Failed to parse AP lookup configuration:', e);
      return;
    }

    // Get DOM elements
    const input = document.getElementById(`ap-reference-${sectionId}`);
    const lookupBtn = document.getElementById(`ap-lookup-btn-${sectionId}`);
    const resultContainer = document.querySelector(`#ap-lookup-${sectionId} .ap-lookup-result`);
    const resultContent = document.querySelector(`#ap-lookup-${sectionId} .ap-result-content`);
    const loadingSpinner = document.querySelector(`#ap-lookup-${sectionId} .ap-loading-spinner`);
    const honeypot = document.querySelector(`#ap-lookup-${sectionId} .ap-honeypot`);
    const notification = document.getElementById(`ap-notification-${sectionId}`);

    if (!input || !lookupBtn || !resultContainer || !resultContent) {
      console.error('Required DOM elements not found');
      return;
    }

    // Load data
    let lookupData = null;
    fetch(config.dataUrl)
      .then(response => response.json())
      .then(data => {
        lookupData = data;
      })
      .catch(error => {
        console.error('Failed to load AP lookup data:', error);
        showNotification('Failed to load reference data. Please refresh the page.', 'error');
      });

    // Event listeners
    lookupBtn.addEventListener('click', handleLookup);
    input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        handleLookup();
      }
    });

    // Auto-format input as user types
    input.addEventListener('input', (e) => {
      const value = e.target.value.toUpperCase();
      e.target.value = value;

      // Update hints based on input length
      updateHints(value.replace(/\./g, '').length);
    });

    // Close notification
    const closeBtn = notification?.querySelector('.notification-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        notification.style.display = 'none';
      });
    }

    function updateHints(length) {
      const hints = document.getElementById(`reference-hints-${sectionId}`);
      if (!hints) return;

      if (length === 0) {
        hints.innerHTML = DOMPurify.sanitize(`
          <p style="margin: 5px 0;">✓ Model code: 5 digits (e.g., 15510, 26420)</p>
          <p style="margin: 5px 0;">✓ Full reference: 17 characters with dots (e.g., 15510ST.OO.1320ST.01)</p>
        `);
      } else if (length >= 1 && length <= 4) {
        hints.innerHTML = DOMPurify.sanitize(`
          <p style="margin: 5px 0;">⌨️ Keep typing model code...</p>
          <p style="margin: 5px 0; color: #999;">Need ${5 - length} more digit(s) for model lookup</p>
        `);
      } else if (length === 5) {
        hints.innerHTML = DOMPurify.sanitize(`
          <p style="margin: 5px 0;">✓ Ready! Click LOOKUP for model info</p>
          <p style="margin: 5px 0; color: #999;">Or continue typing for material, bezel, bracelet details...</p>
        `);
      } else if (length === 6) {
        hints.innerHTML = DOMPurify.sanitize(`
          <p style="margin: 5px 0;">⌨️ 1 more character for case material...</p>
          <p style="margin: 5px 0; color: #999;">Or click LOOKUP now to see what you have</p>
        `);
      } else if (length >= 7 && length < 17) {
        const remaining = 17 - length;
        hints.innerHTML = DOMPurify.sanitize(`
          <p style="margin: 5px 0;">✓ Ready! Click LOOKUP to decode (${length} chars)</p>
          <p style="margin: 5px 0; color: #999;">Or type ${remaining} more for full reference</p>
        `);
      } else if (length === 17) {
        hints.innerHTML = DOMPurify.sanitize(`
          <p style="margin: 5px 0;">✓ Full reference ready!</p>
          <p style="margin: 5px 0;">Click LOOKUP to decode all segments</p>
        `);
      } else {
        hints.innerHTML = DOMPurify.sanitize(`
          <p style="margin: 5px 0;">⚠️ Input too long (${length} chars)</p>
          <p style="margin: 5px 0;">Click LOOKUP to decode (will use first 17 chars)</p>
        `);
      }
    }

    function handleLookup() {
      // Security: Check honeypot
      if (honeypot && honeypot.value !== '') {
        console.warn('Honeypot triggered - possible bot');
        return;
      }

      if (!lookupData) {
        showNotification('Reference data is still loading. Please try again.', 'warning');
        return;
      }

      const rawInput = input.value.trim().toUpperCase();
      if (!rawInput) {
        showNotification('Please enter an AP reference number or model code.', 'warning');
        input.focus();
        return;
      }

      // Show loading
      resultContainer.style.display = 'block';
      loadingSpinner.style.display = 'block';
      resultContent.innerHTML = '';
      resultContainer.setAttribute('aria-busy', 'true');
      lookupBtn.disabled = true;

      // Simulate processing delay for better UX
      setTimeout(() => {
        try {
          const result = performLookup(rawInput, lookupData);
          displayResult(result);
        } catch (error) {
          displayError(error.message);
        } finally {
          loadingSpinner.style.display = 'none';
          resultContainer.setAttribute('aria-busy', 'false');
          lookupBtn.disabled = false;
        }
      }, 300);
    }

    function performLookup(input, data) {
      // Remove all dots and spaces
      const cleaned = input.replace(/[\.\s]/g, '');

      // Determine lookup type based on length - progressive disclosure
      if (cleaned.length === 5 && /^\d{5}$/.test(cleaned)) {
        // Model code only
        return lookupModelCode(cleaned, data);
      } else if (cleaned.length >= 7) {
        // Partial or full reference - parse what we can
        return lookupProgressiveReference(cleaned, data);
      } else {
        throw new Error('Invalid input. Enter at least a 5-digit model code (e.g., 15510) or more characters for detailed decode.');
      }
    }

    function lookupModelCode(code, data) {
      const model = data.modelCodes[code];

      if (!model) {
        throw new Error(`Model code "${code}" not found in database. Please verify the code or try a full reference number.`);
      }

      return {
        type: 'model',
        code: code,
        model: model
      };
    }

    function lookupProgressiveReference(cleaned, data) {
      // Progressive parsing based on available characters
      const length = cleaned.length;
      const segments = {};

      // Warn if too long
      let extraCharsWarning = null;
      if (length > 17) {
        extraCharsWarning = `Input is ${length - 17} characters too long. Using first 17 characters.`;
        cleaned = cleaned.substring(0, 17);
      }

      // Parse Model Code (first 5 chars)
      if (length >= 5) {
        const modelCode = cleaned.substring(0, 5);
        segments.model = {
          code: modelCode,
          data: data.modelCodes[modelCode] || null,
          found: !!data.modelCodes[modelCode]
        };
      }

      // Parse Case Material (chars 6-7)
      if (length >= 7) {
        const caseMaterial = cleaned.substring(5, 7);
        segments.caseMaterial = {
          code: caseMaterial,
          data: data.materials[caseMaterial] || null,
          found: !!data.materials[caseMaterial]
        };
      }

      // Parse Bezel Code (chars 8-9)
      if (length >= 9) {
        const bezelCode = cleaned.substring(7, 9);
        segments.bezel = {
          code: bezelCode,
          data: data.bezels[bezelCode] || null,
          found: !!data.bezels[bezelCode]
        };
      }

      // Parse Bracelet Code + Material (chars 10-15)
      if (length >= 15) {
        const braceletCode = cleaned.substring(9, 13);
        const braceletMaterial = cleaned.substring(13, 15);

        // Check if it's a strap material
        let braceletMaterialData = null;
        let braceletMaterialFound = false;

        if (data.strapMaterials[braceletMaterial]) {
          braceletMaterialData = { type: 'strap', ...data.strapMaterials[braceletMaterial] };
          braceletMaterialFound = true;
        } else if (data.materials[braceletMaterial]) {
          braceletMaterialData = { type: 'bracelet', ...data.materials[braceletMaterial] };
          braceletMaterialFound = true;
        }

        segments.bracelet = {
          code: braceletCode,
          material: braceletMaterial,
          pattern: data.braceletPatterns[braceletCode] || null,
          materialData: braceletMaterialData,
          found: braceletMaterialFound
        };
      }

      // Parse Dial Code (chars 16-17)
      if (length >= 17) {
        const dialCode = cleaned.substring(15, 17);
        segments.dial = {
          code: dialCode,
          found: true  // Dial codes are always valid (sequential)
        };
      }

      // Format with dots for display
      let formatted = '';
      if (length >= 7) {
        formatted = cleaned.substring(0, 7);
        if (length >= 9) formatted += '.' + cleaned.substring(7, 9);
        if (length >= 15) formatted += '.' + cleaned.substring(9, 15);
        if (length >= 17) formatted += '.' + cleaned.substring(15, 17);
      }

      return {
        type: 'progressive',
        formatted: formatted,
        length: length,
        segments: segments,
        extraCharsWarning: extraCharsWarning
      };
    }

    function lookupFullReference(cleaned, data) {
      // Validate length
      if (cleaned.length < 17) {
        throw new Error(`Reference too short. Expected 17 characters, got ${cleaned.length}. Format: XXXXXXX.XX.XXXXXX.XX`);
      }

      // Parse segments: XXXXXXX.XX.XXXXXX.XX
      // Segment 1: 7 chars (5 model + 2 material)
      // Segment 2: 2 chars (bezel)
      // Segment 3: 6 chars (4 bracelet + 2 material)
      // Segment 4: 2 chars (dial)

      const segment1 = cleaned.substring(0, 7);  // 15510ST
      const segment2 = cleaned.substring(7, 9);  // OO
      const segment3 = cleaned.substring(9, 15); // 1320ST
      const segment4 = cleaned.substring(15, 17); // 01

      const modelCode = segment1.substring(0, 5);
      const caseMaterial = segment1.substring(5, 7);
      const bezelCode = segment2;
      const braceletCode = segment3.substring(0, 4);
      const braceletMaterial = segment3.substring(4, 6);
      const dialCode = segment4;

      // Lookup each component
      const model = data.modelCodes[modelCode];
      const material = data.materials[caseMaterial];
      const bezel = data.bezels[bezelCode];

      // Check if bracelet material is a strap code
      let braceletInfo = null;
      if (data.strapMaterials[braceletMaterial]) {
        braceletInfo = {
          type: 'strap',
          ...data.strapMaterials[braceletMaterial]
        };
      } else if (data.materials[braceletMaterial]) {
        braceletInfo = {
          type: 'bracelet',
          ...data.materials[braceletMaterial]
        };
      }

      const braceletPattern = data.braceletPatterns[braceletCode];

      // Format with dots
      const formatted = `${segment1}.${segment2}.${segment3}.${segment4}`;

      return {
        type: 'full',
        formatted: formatted,
        segments: {
          model: {
            code: modelCode,
            material: caseMaterial,
            data: model,
            materialData: material
          },
          bezel: {
            code: bezelCode,
            data: bezel
          },
          bracelet: {
            code: braceletCode,
            material: braceletMaterial,
            pattern: braceletPattern,
            materialData: braceletInfo
          },
          dial: {
            code: dialCode
          }
        }
      };
    }

    function displayResult(result) {
      let html = '';

      if (result.type === 'model') {
        // Model code only result
        html = `
          <div class="ap-success">
            <h3>✓ Model Code Found</h3>
            <div class="ap-segment">
              <div class="ap-segment-title">Model Code</div>
              <div class="ap-segment-value">${escapeHtml(result.code)}</div>
              <div class="ap-segment-description">
                <strong>${escapeHtml(result.model.name)}</strong><br>
                Case Size: ${escapeHtml(result.model.size)}<br>
                ${escapeHtml(result.model.description)}
              </div>
            </div>
            <div class="ap-info-box">
              <p><strong>💡 Tip:</strong> Enter the full reference number (e.g., ${escapeHtml(result.code)}ST.OO.1320ST.01) to decode material, bezel, bracelet, and dial details.</p>
            </div>
          </div>
        `;
      } else if (result.type === 'progressive') {
        // Progressive reference decode - show what we found
        const segments = result.segments;
        const hasWarning = result.extraCharsWarning || Object.values(segments).some(seg => seg && !seg.found);

        html = `
          <div class="${hasWarning ? 'ap-info-box' : 'ap-success'}">
            <h3>${hasWarning ? '🔍' : '✓'} Progressive Reference Decode</h3>
            ${result.formatted ? `
              <p style="font-size: 1.1em; font-weight: bold; text-align: center; font-family: 'Courier New', monospace; color: var(--ap-accent-color, #AF935F); margin-bottom: 15px;">${escapeHtml(result.formatted)}</p>
            ` : ''}
            ${result.extraCharsWarning ? `
              <div class="ap-error" style="margin-bottom: 15px;">
                <p style="margin: 0;">⚠️ ${escapeHtml(result.extraCharsWarning)}</p>
              </div>
            ` : ''}

            ${segments.model ? `
              <div class="ap-segment">
                <div class="ap-segment-title">${segments.model.found ? '✓' : '⚠️'} Model Code: ${escapeHtml(segments.model.code)}</div>
                ${segments.model.found ? `
                  <div class="ap-segment-value">${escapeHtml(segments.model.data.name)} (${escapeHtml(segments.model.data.size)})</div>
                  <div class="ap-segment-description">${escapeHtml(segments.model.data.description)}</div>
                ` : `
                  <div class="ap-segment-description" style="color: #c9302c;">Model code "${escapeHtml(segments.model.code)}" not found in database</div>
                `}
              </div>
            ` : ''}

            ${segments.caseMaterial ? `
              <div class="ap-segment">
                <div class="ap-segment-title">${segments.caseMaterial.found ? '✓' : '⚠️'} Case Material: ${escapeHtml(segments.caseMaterial.code)}</div>
                ${segments.caseMaterial.found ? `
                  <div class="ap-segment-value">${escapeHtml(segments.caseMaterial.data.name)}</div>
                  <div class="ap-segment-description">${escapeHtml(segments.caseMaterial.data.description)}</div>
                ` : `
                  <div class="ap-segment-description" style="color: #c9302c;">Material code "${escapeHtml(segments.caseMaterial.code)}" not found in database</div>
                `}
              </div>
            ` : ''}

            ${segments.bezel ? `
              <div class="ap-segment">
                <div class="ap-segment-title">${segments.bezel.found ? '✓' : '⚠️'} Bezel Code: ${escapeHtml(segments.bezel.code)}</div>
                ${segments.bezel.found ? `
                  <div class="ap-segment-value">${escapeHtml(segments.bezel.data.name)}</div>
                  <div class="ap-segment-description">${escapeHtml(segments.bezel.data.description)}</div>
                ` : `
                  <div class="ap-segment-description" style="color: #c9302c;">Bezel code "${escapeHtml(segments.bezel.code)}" not found in database</div>
                `}
              </div>
            ` : ''}

            ${segments.bracelet ? `
              <div class="ap-segment">
                <div class="ap-segment-title">${segments.bracelet.found ? '✓' : '⚠️'} Bracelet/Strap: ${escapeHtml(segments.bracelet.code + segments.bracelet.material)}</div>
                ${segments.bracelet.pattern ? `
                  <div class="ap-segment-value">${escapeHtml(segments.bracelet.pattern)}</div>
                ` : `
                  <div class="ap-segment-description" style="color: #999;">Code: ${escapeHtml(segments.bracelet.code)}</div>
                `}
                ${segments.bracelet.found && segments.bracelet.materialData ? `
                  <div class="ap-segment-description" style="margin-top: 5px;">
                    <strong>${segments.bracelet.materialData.type === 'strap' ? 'Strap' : 'Bracelet'} Material:</strong> ${escapeHtml(segments.bracelet.materialData.name)}<br>
                    ${escapeHtml(segments.bracelet.materialData.description)}
                  </div>
                ` : !segments.bracelet.found ? `
                  <div class="ap-segment-description" style="color: #c9302c;">Material code "${escapeHtml(segments.bracelet.material)}" not found in database</div>
                ` : ''}
              </div>
            ` : ''}

            ${segments.dial ? `
              <div class="ap-segment">
                <div class="ap-segment-title">✓ Dial Code: ${escapeHtml(segments.dial.code)}</div>
                <div class="ap-segment-value">Dial Variation #${escapeHtml(segments.dial.code)}</div>
                <div class="ap-segment-description">
                  This is the ${getOrdinal(segments.dial.code)} dial variation for this model.
                  Dial codes are sequential and do not indicate specific colors.
                </div>
              </div>
            ` : ''}

            ${result.length < 17 ? `
              <div class="ap-info-box" style="margin-top: 15px;">
                <p style="margin: 0;"><strong>💡 Tip:</strong> Keep typing to decode ${17 - result.length} more characters for a complete reference breakdown.</p>
              </div>
            ` : ''}

            <div class="ap-info-box" style="margin-top: 15px;">
              <p style="margin: 0;"><strong>📍 Remember:</strong> AP reference numbers are not engraved on the watch. Check warranty cards or certificates.</p>
            </div>
          </div>
        `;
      } else if (result.type === 'full') {
        // Full reference decode
        const segments = result.segments;

        html = `
          <div class="ap-success">
            <h3>✓ Full Reference Decoded</h3>
            <p style="font-size: 1.2em; font-weight: bold; text-align: center; font-family: 'Courier New', monospace; color: var(--ap-accent-color, #AF935F); margin-bottom: 20px;">${escapeHtml(result.formatted)}</p>

            <h4>📱 Model & Case Material</h4>
            <div class="ap-segment">
              <div class="ap-segment-title">Segment 1: ${escapeHtml(segments.model.code + segments.model.material)}</div>
              ${segments.model.data ? `
                <div class="ap-segment-value">${escapeHtml(segments.model.data.name)} (${escapeHtml(segments.model.data.size)})</div>
                <div class="ap-segment-description">
                  ${escapeHtml(segments.model.data.description)}
                </div>
              ` : `
                <div class="ap-segment-value">Model ${escapeHtml(segments.model.code)}</div>
                <div class="ap-segment-description" style="color: #c9302c;">⚠️ Model code not in database</div>
              `}
              ${segments.model.materialData ? `
                <div class="ap-segment-description" style="margin-top: 10px;">
                  <strong>Case Material:</strong> ${escapeHtml(segments.model.materialData.name)} (${escapeHtml(segments.model.material)})
                </div>
              ` : `
                <div class="ap-segment-description" style="margin-top: 10px; color: #c9302c;">
                  <strong>Case Material:</strong> ${escapeHtml(segments.model.material)} (unknown code)
                </div>
              `}
            </div>

            <h4>💎 Bezel Type</h4>
            <div class="ap-segment">
              <div class="ap-segment-title">Segment 2: ${escapeHtml(segments.bezel.code)}</div>
              ${segments.bezel.data ? `
                <div class="ap-segment-value">${escapeHtml(segments.bezel.data.name)}</div>
                <div class="ap-segment-description">${escapeHtml(segments.bezel.data.description)}</div>
              ` : `
                <div class="ap-segment-value">Code: ${escapeHtml(segments.bezel.code)}</div>
                <div class="ap-segment-description" style="color: #c9302c;">⚠️ Bezel code not in database</div>
              `}
            </div>

            <h4>⌚ Bracelet/Strap</h4>
            <div class="ap-segment">
              <div class="ap-segment-title">Segment 3: ${escapeHtml(segments.bracelet.code + segments.bracelet.material)}</div>
              ${segments.bracelet.pattern ? `
                <div class="ap-segment-value">${escapeHtml(segments.bracelet.pattern)}</div>
              ` : `
                <div class="ap-segment-value">Code: ${escapeHtml(segments.bracelet.code)}</div>
                <div class="ap-segment-description" style="color: #999;">Bracelet/strap style code</div>
              `}
              ${segments.bracelet.materialData ? `
                <div class="ap-segment-description" style="margin-top: 10px;">
                  <strong>${segments.bracelet.materialData.type === 'strap' ? 'Strap' : 'Bracelet'} Material:</strong> ${escapeHtml(segments.bracelet.materialData.name)} (${escapeHtml(segments.bracelet.material)})<br>
                  ${escapeHtml(segments.bracelet.materialData.description)}
                </div>
              ` : `
                <div class="ap-segment-description" style="margin-top: 10px; color: #c9302c;">
                  <strong>Material:</strong> ${escapeHtml(segments.bracelet.material)} (unknown code)
                </div>
              `}
            </div>

            <h4>🎨 Dial Variation</h4>
            <div class="ap-segment">
              <div class="ap-segment-title">Segment 4: ${escapeHtml(segments.dial.code)}</div>
              <div class="ap-segment-value">Dial Variation #${escapeHtml(segments.dial.code)}</div>
              <div class="ap-segment-description">
                This is the ${getOrdinal(segments.dial.code)} dial variation for this model.
                Dial codes are sequential and do not indicate specific colors.
              </div>
            </div>

            <div class="ap-info-box" style="margin-top: 20px;">
              <p><strong>📍 Important:</strong> AP reference numbers are not engraved on the watch. Check warranty cards, certificates, or service papers for verification.</p>
            </div>
          </div>
        `;
      }

      resultContent.innerHTML = DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['h3', 'h4', 'p', 'div', 'strong', 'br'],
        ALLOWED_ATTR: ['class', 'style']
      });
    }

    function displayError(message) {
      const html = `
        <div class="ap-error">
          <h3>❌ Lookup Error</h3>
          <p>${escapeHtml(message)}</p>
          <div class="ap-info-box" style="background-color: white; margin-top: 15px;">
            <p><strong>Accepted Formats:</strong></p>
            <p>• Model code: 5 digits (e.g., 15510, 26420, 15202)</p>
            <p>• Full reference: 17 characters (e.g., 15510ST.OO.1320ST.01)</p>
          </div>
        </div>
      `;

      resultContent.innerHTML = DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['h3', 'h4', 'p', 'div', 'strong'],
        ALLOWED_ATTR: ['class', 'style']
      });
    }

    function showNotification(message, type = 'error') {
      if (!notification) return;

      const content = notification.querySelector('.notification-content');
      if (content) {
        content.textContent = message;
      }

      notification.className = `ap-notification ${type}`;
      notification.style.display = 'block';

      // Auto-hide after 5 seconds
      setTimeout(() => {
        notification.style.display = 'none';
      }, 5000);
    }

    function escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    function getOrdinal(num) {
      const n = parseInt(num);
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    }
  }
})();
